import { useEffect, useMemo, useState } from "react";
import { router } from "expo-router";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import {
  VnDialog,
  VnDialogContent,
  VnDialogFooter,
  VnDialogHeader,
  VnDialogTitle,
} from "@/components/VnDialog";
import { VnPdfViewer } from "@/components/VnPdfViewer/VnPdfViewer.web";
import { VnScrollView } from "@/components/VnScrollView";
import { VnSignatureField } from "@/components/VnSignatureField/VnSignatureField.web";
import { VnText } from "@/components/VnText/VnText";
import { useSignConsentDocumentsMutation } from "@/data/mutations/consentDocumentMutations";
import { useCreatePatientConsentMutation } from "@/data/mutations/consentsMutations";
import { useListConsentDocumentsQuery } from "@/data/queries/consentDocumentsQueries";
import { useHealthPlanAvailabilityQuery } from "@/data/queries/healthPlansQueries";
import { useCesSurvey } from "@/hooks/useCesSurvey";
import { useMergedPdfUrl } from "@/hooks/useMergedPdfUrl";
import { usePatientUSState } from "@/hooks/usePatientUSState";
import { CesSurveyPrompt } from "@/interactors/CesSurveyPrompt";
import { sendSentryError } from "@/services/sentry";

export const ConsentFormScreen = () => {
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const { data: consentDocs } = useListConsentDocumentsQuery();
  const {
    data: healthPlanCount,
    isSuccess: isHealthPlanAvailabilityResolved,
    isError: hasHealthPlanAvailabilityError,
  } = useHealthPlanAvailabilityQuery();
  const { mutate: signConsentDocuments, isPending } =
    useSignConsentDocumentsMutation();
  const { mutate: createPatientConsent } = useCreatePatientConsentMutation();
  const hasAvailableHealthPlan = (healthPlanCount ?? 0) > 0;
  const isIdahoPatient = usePatientUSState() === "ID";
  const { showCesSurveyThenContinue, cesSurveyProps } = useCesSurvey({
    triggerPoint: "CONSENT_FORM_SIGNED",
  });

  const consentDocumentIds =
    consentDocs?.map(doc => doc.consentDocumentId) ?? [];
  const pdfUrls = useMemo(
    () => consentDocs?.map(doc => doc.consentDocumentUri) ?? [],
    [consentDocs],
  );
  const mergedPdfUrl = useMergedPdfUrl(pdfUrls);
  const hasDocuments = consentDocumentIds.length > 0;

  const handleCloseSignatureModal = () => {
    setIsSignatureDialogOpen(false);
    setSubmitError(null);
  };

  const onSignatureChanged = (dataUri: string | null) => {
    setSignature(dataUri);
  };

  const handleSave = () => {
    try {
      if (!hasDocuments) {
        throw new Error("Consent Document IDs are missing");
      }

      signConsentDocuments(
        {
          consentDocumentIds,
          signatureDataUri: signature!,
        },
        {
          onSuccess: () => {
            handleCloseSignatureModal();
            setIsDocumentDialogOpen(false);

            if (hasAvailableHealthPlan) {
              createPatientConsent({
                consentType: "HEALTH_PLAN_SELECTION",
                isConsented: true,
              });
            }

            showCesSurveyThenContinue(() =>
              router.navigate("/(authed)/registration/check-coverage"),
            );
          },
        },
      );
    } catch (e) {
      sendSentryError({
        error: e,
        message: "Failed to save Consent Document signature",
        shouldThrowError: false,
      });
      setSubmitError(e as Error);
    }
  };

  useEffect(() => {
    setIsSaveDisabled(!signature?.length);
  }, [signature]);

  return (
    <VnBox className="bg-background flex-1">
      <VnScrollView>
        <VnCard className="mx-auto my-6 w-[90%] max-w-5xl">
          <VnBox className="flex flex-col gap-2">
            <VnText
              i18nKey="native:consentDocuments.title"
              variant="headerTwo"
            />
            <VnText
              i18nKey="native:consentDocuments.description"
              variant="lg"
            />
            <VnText
              i18nKey="native:consentDocuments.grantSubtitle"
              variant="lgBold"
            />
            <ul className="ml-2 list-inside list-disc">
              <li>
                <VnText i18nKey="native:consentDocuments.grant1" variant="lg" />
              </li>
              <li>
                <VnText i18nKey="native:consentDocuments.grant2" variant="lg" />
              </li>
              <li>
                <VnText i18nKey="native:consentDocuments.grant3" variant="lg" />
              </li>
              {hasAvailableHealthPlan && (
                <li>
                  <VnText
                    i18nKey="native:consentDocuments.grantHealthPlanSelection"
                    variant="lg"
                  />
                </li>
              )}
              <li>
                <VnText i18nKey="native:consentDocuments.grant4" variant="lg" />
              </li>
              {isIdahoPatient && (
                <li>
                  <VnText
                    i18nKey="native:consentDocuments.signatureCountsAsInitials"
                    variant="lg"
                  />
                </li>
              )}
            </ul>
          </VnBox>

          <VnBox className="mt-6 flex flex-col gap-4">
            <VnButton
              onPress={() => setIsDocumentDialogOpen(true)}
              i18nKey="native:consentDocuments.viewDocument"
              variant="secondary"
            />
            <VnButton
              onPress={() => setIsSignatureDialogOpen(true)}
              i18nKey="native:consentDocuments.addSignature"
              disabled={!hasDocuments || !isHealthPlanAvailabilityResolved}
            />
            {hasHealthPlanAvailabilityError && (
              <VnText
                className="text-center text-red-500"
                i18nKey="native:consentDocuments.healthPlanAvailabilityError"
                variant="sm"
              />
            )}
          </VnBox>
        </VnCard>

        <VnDialog
          open={isDocumentDialogOpen}
          onOpenChange={setIsDocumentDialogOpen}>
          <VnDialogContent className="h-[100dvh] w-screen max-w-full rounded-none border-0 sm:h-[90dvh] sm:max-w-2xl sm:rounded-lg sm:border">
            <VnDialogHeader>
              <VnDialogTitle
                i18nKey="native:consentDocuments.documentTitle"
                primitiveTitleClassName="text-center flex-1"
              />
            </VnDialogHeader>
            {mergedPdfUrl && (
              <VnBox className="flex h-full w-full flex-1 flex-col overflow-y-auto py-4">
                <VnPdfViewer
                  pdfUrl={mergedPdfUrl}
                  className="h-full min-h-52 flex-1"
                />
              </VnBox>
            )}
            <VnDialogFooter>
              <VnButton
                onPress={() => setIsSignatureDialogOpen(true)}
                i18nKey="native:consentDocuments.addSignature"
                disabled={!hasDocuments || !isHealthPlanAvailabilityResolved}
                className="w-full"
              />
            </VnDialogFooter>
          </VnDialogContent>
        </VnDialog>

        <VnDialog
          open={isSignatureDialogOpen}
          onOpenChange={handleCloseSignatureModal}>
          <VnDialogContent>
            <VnDialogHeader>
              <VnDialogTitle i18nKey="native:consentDocuments.addSignature" />
            </VnDialogHeader>
            <VnBox className="flex flex-col gap-4 py-4">
              {submitError && (
                <VnBox className="border border-red-500 bg-red-300">
                  <VnText className="p-2" i18nKey="common:errors.unknown" />
                </VnBox>
              )}
              <VnSignatureField onSignatureChanged={onSignatureChanged} />
            </VnBox>
            <VnDialogFooter className="flex flex-row gap-2">
              <VnButton
                onPress={handleCloseSignatureModal}
                i18nKey="common:cancel"
                variant="outline"
                className="flex-1"
              />
              <VnButton
                onPress={handleSave}
                i18nKey="common:save"
                className="flex-1"
                disabled={isSaveDisabled}
                isLoading={isPending}
              />
            </VnDialogFooter>
          </VnDialogContent>
        </VnDialog>
      </VnScrollView>

      <CesSurveyPrompt {...cesSurveyProps} />
    </VnBox>
  );
};
